import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { ITemplate, TemplateScope } from '../../../extViewmodels';
import {
	ICreateSocialMediaPostRequest,
	IImpersonationContextComponentProps,
	ILocationState,
	ImpersonationContextKey,
} from '../../../models';
import { CampaignType, IEditHtmlNewsletterRequest } from '../../../models/AdminModels';
import { BlogPostRequest } from '../../../models/Blogs';
import {
	useEnvironment,
	useErrorMessages,
	useFullscreenModal,
	useUserSession,
} from '../../../models/hooks/appStateHooks';
import { useTemplateExportMutation } from '../../../queries';
import { HtmlNewsletterViewModel } from '../../../viewmodels/HtmlNewletterViewModels';
import { Button } from '../../components-v2/Button';
import { ComboDropdownTrigger, Dropdown, DropdownContent, DropdownItem } from '../../components-v2/Dropdown';
import { ExportConfirmationModalV2 } from '../ExportConfirmation';
import { SupportedCampaignTypes } from '../../containers/ContentCalendar/models';
import { bs } from '../../styles/styles';
import { ISelectBoxOption } from '../SelectBox';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
}

export const NewCampaignButtons: React.FC<IProps> = () => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const templateExportMutation = useTemplateExportMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			templateExportMutation.reset();
		},
	});

	const onCreateCampaign = () => {
		fullscreenModal.history.push({
			pathname: '/email/campaigns/create/from-scratch',
		});
	};

	const onCreateSocialPost = () => {
		fullscreenModal.history.push({ pathname: '/social-media/post/create' });
	};

	const onExportTemplates = () => {
		if (templateExportMutation.isIdle) {
			templateExportMutation.mutate({});
		}
	};

	const socialEnabled = userSession?.account?.features?.socialMedia?.enabled;
	const templateExportEnabled = userSession?.account?.features?.crossAccountTemplateSharing?.enabled;

	return (
		<>
			<div className={css(styleSheet.buttonContainer)}>
				<button className={css(bs.ctaButton, styleSheet.newCampaignButton)} onClick={onCreateCampaign}>
					<span>{socialEnabled ? 'Create Email Campaign' : 'Create New Campaign'}</span>
				</button>
				{socialEnabled && (
					<button className={css(bs.ctaButton, styleSheet.newCampaignButton)} onClick={onCreateSocialPost}>
						<span>Create Social Media Post</span>
					</button>
				)}
				{templateExportEnabled && (
					<button className={css(bs.ctaButton, styleSheet.newCampaignButton)} onClick={onExportTemplates}>
						<span>Export Templates</span>
					</button>
				)}
			</div>
			{!templateExportMutation.isIdle && (
				<ExportConfirmationModalV2
					mutationResult={templateExportMutation}
					modalProps={{
						isOpen: !templateExportMutation.isIdle,
						onRequestClose: () => templateExportMutation.reset(),
					}}
					title='Export Templates to CSV'
					documentKind='template export'
					subTitle={
						<>
							<div>The export includes template name, type, keywords,</div>
							<div>creation date, and template ID for all your templates.</div>
						</>
					}
				/>
			)}
		</>
	);
};

interface IDropDownProps extends IImpersonationContextComponentProps {
	className?: string;
	supportedCampaignTypes: CampaignType[];
	selectedCampaignType?: CampaignType;
	dropdownDisabled?: boolean;
}

export const NewCampaignButtonsDropDown = inject(ImpersonationContextKey)(function NewCampaignButtonsDropDown({
	impersonationContext,
	supportedCampaignTypes = SupportedCampaignTypes,
	selectedCampaignType,
	dropdownDisabled,
}: IDropDownProps) {
	const isAdminView = useEnvironment()?.appType === 'admin';

	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const templateExportMutation = useTemplateExportMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			templateExportMutation.reset();
		},
	});

	const account = impersonationContext?.isValid ? impersonationContext.account : userSession.account;

	const options = React.useMemo<ISelectBoxOption<CampaignType>[]>(() => {
		const o: ISelectBoxOption<CampaignType>[] = [];
		if (supportedCampaignTypes.includes(CampaignType.Email)) {
			o.push({
				title: 'Email',
				value: CampaignType.Email,
			});
		}
		if (supportedCampaignTypes.includes(CampaignType.Social) && account?.features?.socialMedia?.enabled) {
			o.push({
				title: 'Social Post',
				value: CampaignType.Social,
			});
		}
		if (supportedCampaignTypes.includes(CampaignType.Blog) && account?.features?.blogFeature?.enabled) {
			o.push({
				title: 'Blog Post',
				value: CampaignType.Blog,
			});
		}
		if (
			supportedCampaignTypes.includes(CampaignType.htmlNewsletter) &&
			isAdminView &&
			account?.features?.htmlNewsletter?.enabled
		) {
			o.push({
				title: 'HTML newsletter',
				value: CampaignType.htmlNewsletter,
			});
		}
		return o;
	}, [
		account?.features?.blogFeature?.enabled,
		account?.features?.htmlNewsletter?.enabled,
		account?.features?.socialMedia?.enabled,
		isAdminView,
		supportedCampaignTypes,
	]);

	const [selectedContentType, setSelectedContentType] = React.useState<CampaignType | null>(null);

	React.useEffect(() => {
		if (selectedCampaignType) {
			setSelectedContentType(selectedCampaignType);
		}
	}, [selectedCampaignType]);

	const defaultOption = options[0];
	const currentOption = selectedContentType
		? options.find(opt => opt.value === selectedContentType) || defaultOption
		: defaultOption;

	const getButtonText = (option: ISelectBoxOption<CampaignType>) => {
		switch (option.value) {
			case CampaignType.Email:
				return 'Create Email Campaign';
			case CampaignType.Social:
				return 'Create Social Media Post';
			case CampaignType.Blog:
				return 'Create Blog Post';
			case CampaignType.htmlNewsletter:
				return 'Create HTML Newsletter';
			default:
				return 'Create New Campaign';
		}
	};

	const navigateToPath = (path: string, state?: any) => {
		fullscreenModal?.history.push({ pathname: path, state });
	};

	const onSelectionChanged = (option: ISelectBoxOption<any>) => {
		setSelectedContentType(option.value);
		switch (option.value) {
			case CampaignType.Social:
				onCreateSocialPost();
				break;
			case CampaignType.htmlNewsletter:
				onCreateHtmlNewsletter();
				break;
			case CampaignType.Email:
				onCreateCampaign();
				break;
			case CampaignType.Blog:
				onCreateBlogPost();
				break;
			default:
				break;
		}
	};

	const onPrimaryClick = () => {
		if (currentOption) {
			onSelectionChanged(currentOption);
		}
	};

	const onCreateHtmlNewsletter = async () => {
		if (isAdminView && impersonationContext?.account?.features?.htmlNewsletter?.enabled) {
			const template = await HtmlNewsletterViewModel.createDefaultBlankTemplate(userSession, {
				name: 'Untitled Newsletter',
				scope: TemplateScope.Account,
			});

			const locationState: ILocationState<any, IEditHtmlNewsletterRequest> = {
				model: {
					impersonationContext,
					type: 'Template',
					template,
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${impersonationContext?.account?.id}/htmlnewsletter/new`,
				state: locationState,
			});
			return;
		}
	};

	const onCreateBlogPost = () => {
		if (isAdminView) {
			const locationState: ILocationState<any, BlogPostRequest> = {
				model: {
					impersonationContext,
				},
			};
			navigateToPath(`/accounts/${impersonationContext?.account?.id}/content-calendar/blog/post/create`, locationState);
			return;
		}
		navigateToPath('/blog/post/create');
	};

	const onCreateCampaign = () => {
		if (isAdminView) {
			navigateToPath(`/accounts/${impersonationContext?.account?.id}/content-calendar/campaigns/create`);
			return;
		}
		navigateToPath('/email/campaigns/create/from-scratch');
	};

	const onCreateSocialPost = () => {
		if (isAdminView) {
			const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<ITemplate>> = {
				model: {
					context: {},
					impersonationContext,

					sendFromUser: impersonationContext.user,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/accounts/${impersonationContext?.account?.id}/content-calendar/post/create`,
				state: locationStateSocial,
			});
			return;
		}

		navigateToPath('/social-media/post/create');
	};

	const onExportTemplates = () => {
		if (templateExportMutation.isIdle) {
			templateExportMutation.mutate({});
		}
	};

	const templateExportEnabled = account?.features?.crossAccountTemplateSharing?.enabled;

	return (
		<>
			<div className={css(styleSheet.buttonContainer)}>
				{options.length > 1 ? (
					<Dropdown disabled={dropdownDisabled}>
						<ComboDropdownTrigger
							onPrimaryClick={onPrimaryClick}
							variant='primary'
							size='default'
							styles={[styleSheet.buttonWrapper]}
						>
							{getButtonText(currentOption)}
						</ComboDropdownTrigger>
						<DropdownContent align='end'>
							{options
								.filter(option => option.value !== currentOption?.value)
								.map(option => (
									<DropdownItem key={option.value} onClick={() => onSelectionChanged(option)}>
										{getButtonText(option)}
									</DropdownItem>
								))}
						</DropdownContent>
					</Dropdown>
				) : (
					<Button
						variant='primary'
						size='default'
						styles={[styleSheet.fullWidthButton]}
						onClick={() => onSelectionChanged(options[0])}
					>
						{getButtonText(options[0])}
					</Button>
				)}
				{templateExportEnabled && (
					<Button
						variant='secondary'
						size='default'
						onClick={onExportTemplates}
						disabled={!templateExportMutation.isIdle}
					>
						Export Templates
					</Button>
				)}
			</div>
			{!templateExportMutation.isIdle && (
				<ExportConfirmationModalV2
					mutationResult={templateExportMutation}
					modalProps={{
						isOpen: !templateExportMutation.isIdle,
						onRequestClose: () => templateExportMutation.reset(),
					}}
					title='Export Templates to CSV'
					documentKind='template export'
					subTitle={
						<>
							<div>The export includes template name, type, keywords,</div>
							<div>creation date, and template ID for all your templates.</div>
						</>
					}
				/>
			)}
		</>
	);
});
